/* andCharlie product-level wishlist. No account credentials or third-party scripts. */
(() => {
  'use strict';
  if (window.acWishlist) return;
  const config = document.querySelector('[data-ac-wishlist-config]');
  if (!config) return;
  const root = config.dataset.root || '/';
  const customer = config.dataset.customerId || '';
  const syncEnabled = config.dataset.syncEnabled === 'true';
  const prefix = 'ac-wishlist-v1:';
  const guestPrefix = `${prefix}guest:`;
  const pendingPrefix = `${prefix}pending:${customer}:`;
  const snapshotKey = `${prefix}account:${customer}`;
  const maxItems = 500;
  const batchSize = 16;
  const page = document.querySelector('[data-ac-wishlist-page]');
  const grid = page?.querySelector('[data-ac-wishlist-grid]');
  const memory = new Map();
  const productCards = new Map();
  const productKey = (product) => `${product.id}:${product.handle}`;
  let persistent = true;
  let syncing = false;
  let toastTimer;
  let syncTimer;
  let renderVersion = 0;
  let visibleCount = batchSize;
  let renderedKey = '';
  let controller;
  let refreshQueued = false;
  let lastSync = 0;
  const read = (key, fallback = null) => {
    try { return JSON.parse(persistent ? localStorage.getItem(key) : memory.get(key)) ?? fallback; }
    catch { return fallback; }
  };
  const keys = () => {
    try { return persistent ? Object.keys(localStorage) : [...memory.keys()]; }
    catch { persistent = false; return [...memory.keys()]; }
  };
  const write = (key, value) => {
    if (persistent) {
      try { localStorage.setItem(key, JSON.stringify(value)); return; }
      catch {
        // Preserve readable data in memory if quota/privacy settings reject writes.
        for (const existing of keys().filter((k) => k.startsWith(prefix))) {
          try { memory.set(existing, localStorage.getItem(existing)); } catch { /* unavailable */ }
        }
        persistent = false;
      }
    }
    memory.set(key, JSON.stringify(value));
  };
  const remove = (key) => {
    try { if (persistent) localStorage.removeItem(key); else memory.delete(key); }
    catch { /* Keep unsynced entries for retry. */ }
  };
  const valid = (p) => p && /^\d{1,20}$/.test(p.id) && /^[a-z0-9][a-z0-9-]{0,254}$/.test(p.handle);
  const readList = (key) => { const value = read(key, []); return Array.isArray(value) ? value : []; };
  const guest = () => keys().filter((k) => k.startsWith(guestPrefix)).map((k) => read(k)).filter((p) => valid(p?.product)).sort((a, b) => a.created - b.created);
  const pending = () => keys().filter((k) => k.startsWith(pendingPrefix)).map((k) => read(k)).filter((o) => valid(o?.product)).sort((a, b) => a.created - b.created || a.opId.localeCompare(b.opId));
  const items = () => {
    const map = new Map();
    if (customer) for (const p of readList(snapshotKey).filter(valid)) map.set(p.id, p);
    for (const o of guest()) map.set(o.product.id, o.product);
    if (customer) for (const o of pending()) {
      if (o.saved) map.set(o.product.id, o.product); else map.delete(o.product.id);
    }
    return [...map.values()];
  };
  const toast = (message) => {
    const target = document.querySelector('[data-ac-wishlist-toast]');
    if (!target) return;
    clearTimeout(toastTimer);
    target.textContent = message;
    toastTimer = setTimeout(() => { target.textContent = ''; }, 3200);
  };
  const operation = (product, saved) => ({ opId: crypto.randomUUID(), created: Date.now(), product, saved });
  const putPending = (op) => write(`${pendingPrefix}${op.opId}`, op);
  function toggle(product) {
    if (!valid(product)) return;
    const list = items();
    const saved = !list.some((p) => p.id === product.id);
    if (saved && list.length >= maxItems) { toast('お気に入りは500件まで保存できます。'); return; }
    const op = operation(product, saved);
    if (customer) putPending(op);
    else if (saved) write(`${guestPrefix}${product.id}`, op);
    else remove(`${guestPrefix}${product.id}`);
    toast(persistent ? (saved ? 'お気に入りに追加しました' : 'お気に入りから削除しました') : 'このブラウザでは保存できないため、ページを閉じるまでのお気に入りです。');
    refresh();
    scheduleSync();
  }
  function refreshButtons() {
    const saved = new Set(items().map((p) => p.id));
    document.querySelectorAll('[data-ac-wishlist-button]').forEach((button) => {
      const active = saved.has(button.dataset.productId);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${button.dataset.productTitle || 'この商品'}をお気に入り${active ? 'から削除' : 'に追加'}`);
      const label = button.querySelector('[data-ac-wishlist-label]');
      if (label) label.textContent = active ? 'お気に入り登録済み' : 'お気に入りに追加';
    });
    document.querySelectorAll('[data-ac-wishlist-link]').forEach((link) => {
      link.hidden = false;
      link.setAttribute('aria-label', `お気に入り${saved.size ? `（${saved.size}件）` : ''}`);
    });
    document.querySelectorAll('[data-ac-wishlist-count]').forEach((badge) => {
      badge.hidden = saved.size === 0;
      badge.textContent = saved.size > 99 ? '99+' : String(saved.size);
    });
  }
  function refresh(force = false) { refreshButtons(); if (page) render(force); }
  async function request(url, options = {}) {
    const timeout = AbortSignal.timeout(12000);
    const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout;
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response;
  }
  function note(message) {
    const target = page?.querySelector('[data-ac-wishlist-sync-note]');
    if (target) target.textContent = message;
  }
  function scheduleSync() {
    if (!customer || !syncEnabled) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 250);
  }
  async function sync() {
    if (syncing || !customer || !syncEnabled) return;
    syncing = true;
    let succeeded = false;
    const execute = async () => {
      const endpoint = new URL(config.dataset.proxy, location.origin);
      if (endpoint.origin !== location.origin || !endpoint.pathname.startsWith('/apps/')) throw new Error('Invalid proxy');
      const state = await (await request(endpoint)).json();
      if (String(state.customerId) !== customer || !Array.isArray(state.items) || !state.csrf) throw new Error('Session mismatch');
      write(snapshotKey, state.items.filter(valid));
      const imports = guest();
      const queued = pending();
      // Guest additions precede account operations, so a removal before first sync stays removed.
      const operations = [...imports, ...queued].filter((o, i, all) => all.findIndex((p) => p.opId === o.opId) === i);
      let token = state.csrf;
      for (let start = 0; start < operations.length; start += 100) {
        const batch = operations.slice(start, start + 100);
        const response = await (await request(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ csrf: token, operations: batch }) })).json();
        if (String(response.customerId) !== customer || !Array.isArray(response.items) || !Array.isArray(response.acknowledged)) throw new Error('Invalid response');
        write(snapshotKey, response.items.filter(valid));
        token = response.csrf;
        for (const id of response.acknowledged) {
          remove(`${pendingPrefix}${id}`);
          const imported = imports.find((o) => o.opId === id);
          if (imported && read(`${guestPrefix}${imported.product.id}`)?.opId === id) remove(`${guestPrefix}${imported.product.id}`);
        }
      }
      lastSync = Date.now();
      note(persistent ? 'アカウントに保存しました。別の端末でも確認できます。' : 'アカウントに保存しました。');
      refresh();
    };
    try {
      if (navigator.locks) await navigator.locks.request(`${prefix}sync:${customer}`, execute); else await execute();
      succeeded = true;
    } catch {
      note('アカウントと同期できませんでした。このブラウザの変更は保持しています。通信が戻ったら再試行します。');
      refresh();
    } finally {
      syncing = false;
      if (succeeded && (pending().length || guest().length)) scheduleSync();
    }
  }
  function unavailable(product) {
    const box = document.createElement('div');
    box.className = 'ac-wishlist-unavailable';
    const title = document.createElement('p'); title.textContent = product.title || '保存した商品';
    const message = document.createElement('p'); message.textContent = '現在この商品は公開されていません。';
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'button button--secondary';
    button.textContent = 'お気に入りから削除'; button.addEventListener('click', () => toggle(product));
    box.append(title, message, button); return box;
  }
  async function render(force = false) {
    if (force) productCards.clear();
    const list = items();
    const visible = list.slice(0, visibleCount);
    const key = JSON.stringify(visible.map((p) => [p.id, p.handle]));
    page.querySelector('[data-ac-wishlist-more]').hidden = list.length <= visibleCount;
    page.querySelector('[data-ac-wishlist-empty]').hidden = list.length !== 0;
    if (!force && renderedKey === key) return;
    const version = ++renderVersion;
    controller?.abort(); controller = new AbortController();
    const message = page.querySelector('[data-ac-wishlist-message]');
    const retry = page.querySelector('[data-ac-wishlist-retry]');
    retry.hidden = true;
    if (!list.length) {
      const needsFocus = grid.contains(document.activeElement);
      grid.replaceChildren(); grid.removeAttribute('aria-busy'); message.textContent = ''; renderedKey = key;
      if (needsFocus) document.querySelector('#ac-wishlist-title')?.focus({ preventScroll: true });
      return;
    }
    message.textContent = 'お気に入りを読み込んでいます。';
    grid.setAttribute('aria-busy', 'true');
    try {
      const missing = visible.filter((product) => !productCards.has(productKey(product)));
      for (let i = 0; i < missing.length; i += batchSize) {
        const batch = missing.slice(i, i + batchSize);
        const url = new URL(`${root}search`, location.origin);
        url.searchParams.set('section_id', 'andcharlie-wishlist-products');
        url.searchParams.set('q', batch.map((p) => p.handle).join(','));
        url.searchParams.set('type', 'product');
        const html = await (await request(url, { signal: controller.signal })).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('[data-ac-wishlist-products]') || /Liquid error/i.test(html)) throw new Error('Invalid product response');
        if (version !== renderVersion) return;
        const returned = [...doc.querySelectorAll('[data-ac-wishlist-result]')];
        batch.forEach((product) => productCards.set(productKey(product), returned.find((node) => node.dataset.acWishlistResult === product.id && node.dataset.acWishlistHandle === product.handle) || null));
      }
      if (version !== renderVersion) return;
      const focusedId = grid.contains(document.activeElement) ? document.activeElement.closest('[data-ac-wishlist-item]')?.dataset.acWishlistItem : null;
      const fragment = document.createDocumentFragment();
      for (const product of visible) {
        const item = document.createElement('li'); item.className = 'grid__item'; item.dataset.acWishlistItem = product.id;
        item.append(productCards.get(productKey(product))?.cloneNode(true) || unavailable(product)); fragment.append(item);
      }
      grid.replaceChildren(fragment);
      renderedKey = key;
      message.textContent = `${list.length}件のお気に入り`;
      refreshButtons();
      if (focusedId) (grid.querySelector(`[data-ac-wishlist-item="${focusedId}"] button`) || grid.querySelector('button') || document.querySelector('#ac-wishlist-title'))?.focus({ preventScroll: true });
    } catch (error) {
      if (version !== renderVersion || error.name === 'AbortError') return;
      // A transient failure must never delete saved products or present stale prices as current.
      grid.replaceChildren(); renderedKey = '';
      message.textContent = '商品情報を読み込めませんでした。お気に入りは保存されています。'; retry.hidden = false;
    } finally { if (version === renderVersion) grid.removeAttribute('aria-busy'); }
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ac-wishlist-button]');
    if (!button) return;
    event.preventDefault(); event.stopPropagation();
    toggle({ id: button.dataset.productId, handle: button.dataset.productHandle, title: button.dataset.productTitle });
  });
  page?.querySelector('[data-ac-wishlist-more]').addEventListener('click', () => { visibleCount += batchSize; render(); });
  page?.querySelector('[data-ac-wishlist-retry]').addEventListener('click', () => { render(true); scheduleSync(); });
  window.addEventListener('storage', (event) => { if (!event.key || event.key.startsWith(prefix)) refresh(); });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    if (customer) { location.reload(); return; }
    refresh(true); scheduleSync();
  });
  window.addEventListener('online', scheduleSync);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && Date.now() - lastSync > 30000) { refresh(true); scheduleSync(); }
  });
  // Dawn replaces filters, quick views and product information without a full navigation.
  new MutationObserver((records) => {
    if (!records.some((r) => [...r.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.('[data-ac-wishlist-button],[data-ac-wishlist-link]') || node.querySelector?.('[data-ac-wishlist-button],[data-ac-wishlist-link]'))))) return;
    if (refreshQueued) return;
    refreshQueued = true; queueMicrotask(() => { refreshQueued = false; refreshButtons(); });
  }).observe(document.body, { childList: true, subtree: true });
  window.acWishlist = { refresh: () => refresh(true), sync };
  refresh(); scheduleSync();
})();
